/* eslint-disable @typescript-eslint/no-use-before-define */
import { array, option, record } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import {
  AtomicEntry,
  AtomicRecord,
  DecomposableRecord,
  NodeTrees,
  PossibleNodeTypes,
} from '../../types';
import { mergeRecords, mergeRecordsWithParent } from './mergeUtils';

/**
 * Convert a NodeTrees object (i.e. JSON)  into a Collapsed State record for localStorage
 */

// Type Guard to detect if the value is a Record<string, unknown>
function isRecord(
  value: PossibleNodeTypes,
): value is AtomicRecord | (DecomposableRecord & Record<string, unknown>) {
  return (
    !Array.isArray(value) &&
    typeof value === 'object' &&
    value !== null &&
    Object.keys(value).length > 0
  );
}

// Helper function to route to the correct conversion method
function convertNodeRecordValue(
  value: PossibleNodeTypes,
  path = '',
  collapseByDefault = false,
): option.Option<Record<string, boolean>> {
  if (Array.isArray(value)) {
    return option.fromNullable(convertNodeArray(value, path, collapseByDefault));
  }

  return isRecord(value)
    ? option.fromNullable(convertNodeRecord(value, path, collapseByDefault))
    : option.none;
}

function convertNodeArray(
  values: Array<AtomicEntry | AtomicRecord | DecomposableRecord>,
  path = '',
  collapseByDefault = false,
): Record<string, boolean> {
  return pipe(
    values,
    array.filterMapWithIndex(
      (
        i,
        v, // Filter out leaf entries and convert branch entries into Record<string, boolean>
      ) => convertNodeRecordValue(v, `${path}${path === '' ? '' : '.'}${i}`, collapseByDefault),
    ),
    array.reduce({} as Record<string, boolean>, mergeRecords), // Merge the Record<string, boolean> entries into one
    mergeRecordsWithParent(path, collapseByDefault), // Merge the children entries with a parent entry
  );
}

export function convertNodeRecord(
  value?: NodeTrees,
  path = '',
  collapseByDefault = false,
): Record<string, boolean> {
  return pipe(
    value,
    option.fromNullable, // Convert to an Option
    option.map(record.toArray), // Convert the record to an array of entries
    option.map(
      // Filter leaf entries and convert the branch entries into Record<string, boolean>
      array.filterMap(([i, v]) =>
        convertNodeRecordValue(v, `${path}${path === '' ? '' : '.'}${i}`, collapseByDefault),
      ),
    ),
    option.map(array.reduce({} as Record<string, boolean>, mergeRecords)), // Merge the records into one Record<string, boolean>
    option.getOrElse(() => ({})), // Ensure we have an Record<string, boolean>
    mergeRecordsWithParent(path, collapseByDefault), // Merge the child records created above with the parent entry
  );
}
